import React, { useState, createContext, useContext, useEffect } from "react";

const IS_DEBUGGING = false;

export const LCP_TIMEOUT = 7000; //TODO: tweak it
export const USER_EVENTS_TO_TRACK = ["scroll", "touchstart", "mousemove"] as const;

const conditionallyLog = (message: string) => {
  if (IS_DEBUGGING) {
    console.log(message);
  }
};

type LazyLoadingContextType = {
  hasVisuallyLoaded: boolean;
  hasUserInteracted: boolean;
};

const LazyLoadingContext = createContext<LazyLoadingContextType>({
  hasVisuallyLoaded: false,
  hasUserInteracted: false,
});

export const LazyLoadingProvider = ({ children }: { children: React.ReactNode }) => {
  const [hasVisuallyLoaded, setHasVisuallyLoaded] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    let lcpObserver: PerformanceObserver | null = null;
    let timeout: ReturnType<typeof setTimeout>;

    const handleLCP = () => {
      setHasVisuallyLoaded(true);
      conditionallyLog("LCP detected");
      clearTimeout(timeout);
    };

    const setupLCPObserver = () => {
      try {
        if (typeof PerformanceObserver !== "undefined") {
          lcpObserver = new PerformanceObserver((entryList) => {
            for (const entry of entryList.getEntries()) {
              conditionallyLog(`LCP detected: ${entry.startTime}`);
              handleLCP();
            }
          });

          lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
        } else {
          conditionallyLog("PerformanceObserver not supported");
        }
      } catch (error) {
        conditionallyLog(`Error setting up LCP observer: ${error}`);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Reset the LCP observer when the page becomes visible
        if (lcpObserver) {
          lcpObserver.disconnect();
        }
        conditionallyLog("LCP observer reset once visibility change");
        setupLCPObserver();

        // Reset the timeout
        clearTimeout(timeout);
        timeout = setTimeout(handleLCP, LCP_TIMEOUT);
      }
    };

    setupLCPObserver();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // If the LCP is not detected, we will set hasVisuallyLoaded to true after the timeout to be safe
    timeout = setTimeout(() => {
      conditionallyLog("LCP timeout reached");
      if (!hasVisuallyLoaded) {
        handleLCP();
      }
    }, LCP_TIMEOUT);

    return () => {
      if (lcpObserver) {
        lcpObserver.disconnect();
      }
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cleanup = () => {
      USER_EVENTS_TO_TRACK.forEach((event) => {
        window.removeEventListener(event, handleUserInteraction);
      });
    };

    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      conditionallyLog("User interaction detected");
      cleanup();
    };

    USER_EVENTS_TO_TRACK.forEach((event) => {
      window.addEventListener(event, handleUserInteraction, { passive: true });
    });

    return () => {
      cleanup();
    };
  }, []);

  return (
    <LazyLoadingContext.Provider value={{ hasVisuallyLoaded, hasUserInteracted }}>
      {children}
    </LazyLoadingContext.Provider>
  );
};

/**
 * A React hook that provides access to the application loading state information so you can use it to lazy load features.
 *
 * It provides two pieces of state:
 * - hasVisuallyLoaded: boolean indicating if the Largest Contentful Paint (LCP) has been detected
 * - hasUserInteracted: boolean indicating if the user has performed any tracked interactions
 *
 * @returns {Object} An object containing:
 *   - hasVisuallyLoaded: boolean - True when LCP is detected or timeout is reached
 *   - hasUserInteracted: boolean - True when user performs any of the tracked events (scroll, touchstart, mousemove)
 *
 * @throws {Error} If used outside of a LazyLoadingProvider component
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { hasVisuallyLoaded, hasUserInteracted } = useLazyLoading();
 *
 *   return (
 *     <div>
 *       {hasVisuallyLoaded && <HeavyComponent />}
 *       {hasUserInteracted && <InteractiveComponent />}
 *     </div>
 *   );
 * };
 * ```
 */
export const useLazyLoading = () => {
  const context = useContext(LazyLoadingContext);

  if (context === undefined) {
    throw new Error("useLazyLoading must be used within a LazyLoadingProvider");
  }
  return context;
};
